import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  useModelCostControllerCatalogQuery,
  useModelCostControllerCompanyPolicyQuery,
  useModelCostControllerPriceMutation,
  useModelCostControllerPolicyMutation,
  useModelCostControllerCompanyPricingMutation,
  type PublishModelPriceDto,
  type PublishCostPolicyDto,
} from '@shared/services/apiService';
import { billingErrorMessage } from '../model/billingError';

const units = [
  'llm-token-in',
  'llm-cached-tokens',
  'llm-token-out',
  'stt-second',
  'tts-character',
  'stt-audio-token',
  'tts-audio-token',
];
const initialPrice = (): PublishModelPriceDto => ({
  provider: '',
  model: '',
  unit: 'llm-token-in',
  amount: '',
  per: '',
  granularity: '1',
  source: '',
  verifiedAt: new Date().toISOString(),
  effectiveAt: new Date().toISOString(),
  expectedVersionId: 'none',
});

export const ModelCostAdmin = ({ companyId }: { companyId?: string }) => {
  const [historyOffset, setHistoryOffset] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const global = useModelCostControllerCatalogQuery(
    { offset: historyOffset },
    { skip: !!companyId, refetchOnMountOrArgChange: true },
  );
  const company = useModelCostControllerCompanyPolicyQuery(
    { companyId: companyId ?? '', offset: historyOffset },
    { skip: !companyId, refetchOnMountOrArgChange: true },
  );
  const query = companyId ? company : global;
  const data = query.currentData;
  const [publishPrice, priceState] = useModelCostControllerPriceMutation();
  const [publishDefault, defaultState] = useModelCostControllerPolicyMutation();
  const [publishCompany, companyState] = useModelCostControllerCompanyPricingMutation();
  const [price, setPrice] = useState<PublishModelPriceDto | null>(null);
  const [policy, setPolicy] = useState<PublishCostPolicyDto | null>(null);
  const [error, setError] = useState('');
  const busy = priceState.isLoading || defaultState.isLoading || companyState.isLoading;
  const save = async () => {
    setError('');
    try {
      if (price) {
        const previous = data?.prices.find(
          (p) => p.provider === price.provider && p.model === price.model && p.unit === price.unit,
        );
        await publishPrice({
          publishModelPriceDto: {
            ...price,
            expectedVersionId:
              price.expectedVersionId !== 'none'
                ? price.expectedVersionId
                : (previous?.latestVersionId ?? previous?.id ?? 'none'),
          },
        }).unwrap();
      } else if (policy) {
        const body = {
          ...policy,
          ...(policy.mode === 'duration' && companyId
            ? { markup: undefined, usdPerCredit: undefined }
            : {}),
        };
        if (companyId) await publishCompany({ companyId, publishCostPolicyDto: body }).unwrap();
        else await publishDefault({ publishCostPolicyDto: body }).unwrap();
      }
      setPrice(null);
      setPolicy(null);
      await query.refetch();
    } catch (e) {
      const response = e as { status?: number; data?: { message?: unknown } };
      const message = response.data?.message;
      setError(
        (response.status === 400 || response.status === 409) &&
          (typeof message === 'string' || Array.isArray(message))
          ? Array.isArray(message)
            ? message.filter((v) => typeof v === 'string').join('; ')
            : (message as string)
          : billingErrorMessage(e),
      );
    }
  };
  const editPolicy = () => {
    const p = data?.policy;
    setError('');
    setPolicy({
      mode: (p?.mode ?? 'duration') as PublishCostPolicyDto['mode'],
      markup: p?.markup ?? '',
      usdPerCredit: p?.usdPerCredit ?? data?.defaultPolicy?.usdPerCredit ?? '',
      effectiveAt: new Date().toISOString(),
      reason: '',
      expectedVersionId: data?.latestPolicyVersionId ?? 'none',
    });
  };
  return (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Box>
          <Typography variant="h6">
            {companyId ? 'Company usage pricing' : 'Model costs and usage pricing'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Versioned USD provider costs. Prices and markup are private to platform operators.
          </Typography>
        </Box>
        <Button variant="outlined" disabled={!data} onClick={editPolicy}>
          {companyId ? 'Set company policy' : 'Set default policy'}
        </Button>
      </Stack>
      {query.isError && (
        <Alert severity="error">Unable to load pricing. Reopen this page to retry.</Alert>
      )}
      {query.isLoading && <Typography>Loading pricing…</Typography>}
      {data && (
        <Typography variant="body2">
          {data.policy
            ? `${data.policy.companyId ? 'Company override' : 'Default policy'} · ${data.policy.mode} · ${data.policy.markup ?? '—'}× markup · $${data.policy.usdPerCredit ?? '—'}/credit`
            : 'No approved pricing policy. Existing duration charging remains active.'}
        </Typography>
      )}
      {!companyId && (
        <>
          <Box>
            <Button
              variant="contained"
              onClick={() => {
                setError('');
                setPrice(initialPrice());
              }}>
              Add price version
            </Button>
          </Box>
          <TableContainer>
            <Table size="small" aria-label="Provider model prices">
              <TableHead>
                <TableRow>
                  {[
                    'Provider / model',
                    'Dimension',
                    'USD price',
                    'Per units',
                    'Effective',
                    'Source',
                    '',
                  ].map((h, i) => (
                    <TableCell key={i}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.prices.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell title={`Version ${p.id}; operator ${p.actorId}`}>
                      {p.provider} / {p.model}
                    </TableCell>
                    <TableCell>{p.unit}</TableCell>
                    <TableCell>${p.amount}</TableCell>
                    <TableCell>{p.per}</TableCell>
                    <TableCell>{new Date(p.effectiveAt).toLocaleDateString()}</TableCell>
                    <TableCell sx={{ maxWidth: 240, overflowWrap: 'anywhere' }}>
                      {p.source}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        onClick={() => {
                          setError('');
                          setPrice({
                            ...p,
                            unit: p.unit as PublishModelPriceDto['unit'],
                            effectiveAt: new Date().toISOString(),
                            verifiedAt: new Date().toISOString(),
                            expectedVersionId: p.latestVersionId ?? p.id,
                          });
                        }}>
                        New version
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {data && !data.prices.length && (
                  <TableRow>
                    <TableCell colSpan={7}>No verified model prices have been added.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      <Box>
        <Button onClick={() => setShowHistory(!showHistory)}>
          {showHistory ? 'Hide version history' : 'Version history'}
        </Button>
      </Box>
      {showHistory && data && (
        <Stack spacing={1}>
          <Typography variant="subtitle2">Immutable pricing history</Typography>
          <TableContainer>
            <Table size="small" aria-label="Pricing version history">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Effective</TableCell>
                  <TableCell>Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!companyId &&
                  data.priceHistory.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        {p.provider} / {p.model} / {p.unit}
                      </TableCell>
                      <TableCell>{new Date(p.effectiveAt).toLocaleString()}</TableCell>
                      <TableCell>
                        ${p.amount} per {p.per} · {p.source}
                      </TableCell>
                    </TableRow>
                  ))}
                {data.policyHistory.map((p) => (
                  <TableRow key={p.id}>
                    <TableCell title={`Version ${p.id}; operator ${p.actorId}`}>
                      {p.companyId ? 'Company' : 'Default'} · {p.mode}
                    </TableCell>
                    <TableCell>{new Date(p.effectiveAt).toLocaleString()}</TableCell>
                    <TableCell>
                      {p.markup ?? '—'}× · ${p.usdPerCredit ?? '—'}/credit · {p.reason}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Stack direction="row" spacing={1}>
            <Button
              disabled={historyOffset === 0}
              onClick={() => setHistoryOffset(Math.max(0, historyOffset - 50))}>
              Previous
            </Button>
            <Button
              disabled={data.nextHistoryOffset === null}
              onClick={() => setHistoryOffset(data.nextHistoryOffset!)}>
              Next
            </Button>
          </Stack>
        </Stack>
      )}
      <Dialog
        open={!!price || !!policy}
        onClose={() => {
          if (!busy) {
            setPrice(null);
            setPolicy(null);
          }
        }}
        fullWidth
        maxWidth="sm">
        <DialogTitle>
          {price ? 'Publish provider price' : 'Publish usage pricing policy'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            {price && (
              <>
                {(['provider', 'model', 'amount', 'per', 'granularity', 'source'] as const).map(
                  (field) => (
                    <TextField
                      key={field}
                      label={
                        {
                          provider: 'Provider key',
                          model: 'Model key',
                          amount: 'USD amount',
                          per: 'Per units (e.g. 1000000 tokens)',
                          granularity: 'Provider billing granularity',
                          source: 'Provider price URL or contract reference',
                        }[field]
                      }
                      value={price[field]}
                      onChange={(e) => setPrice({ ...price, [field]: e.target.value })}
                      required
                    />
                  ),
                )}
                <TextField
                  select
                  label="Usage dimension"
                  value={price.unit}
                  onChange={(e) =>
                    setPrice({ ...price, unit: e.target.value as PublishModelPriceDto['unit'] })
                  }>
                  {units.map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  label="Effective at (ISO timestamp)"
                  value={price.effectiveAt}
                  onChange={(e) => setPrice({ ...price, effectiveAt: e.target.value })}
                />
                <Typography variant="body2" color="text.secondary">
                  Publishing confirms you verified this price against the provider. Existing calls
                  retain their captured price.
                </Typography>
              </>
            )}
            {policy && (
              <>
                <TextField
                  select
                  label="Accounting mode"
                  value={policy.mode}
                  onChange={(e) =>
                    setPolicy({ ...policy, mode: e.target.value as PublishCostPolicyDto['mode'] })
                  }>
                  <MenuItem value="duration">Current duration charging</MenuItem>
                  {companyId && (
                    <MenuItem value="shadow">Shadow comparison — no change to charges</MenuItem>
                  )}
                  {companyId && <MenuItem value="resource">Actual resource charging</MenuItem>}
                </TextField>
                {(!companyId || policy.mode !== 'duration') && (
                  <>
                    <TextField
                      label="Cost multiplier"
                      helperText="1.50 means provider cost +50%."
                      value={policy.markup}
                      onChange={(e) => setPolicy({ ...policy, markup: e.target.value })}
                    />
                    <TextField
                      label="USD per credit (global conversion)"
                      disabled={!!companyId}
                      value={policy.usdPerCredit}
                      onChange={(e) => setPolicy({ ...policy, usdPerCredit: e.target.value })}
                    />
                  </>
                )}
                {policy.mode === 'resource' && (
                  <>
                    <Alert severity="warning">
                      Enable only after verified runtime completion coverage and approved commercial
                      settings. New calls use the policy; active calls retain theirs.
                    </Alert>
                    <TextField
                      label="Initial reserve (atomic credit units)"
                      value={policy.minimumUnits ?? ''}
                      onChange={(e) => setPolicy({ ...policy, minimumUnits: e.target.value })}
                    />
                    <TextField
                      type="number"
                      label="Maximum usage staleness (seconds)"
                      value={policy.staleSeconds ?? ''}
                      onChange={(e) =>
                        setPolicy({ ...policy, staleSeconds: Number(e.target.value) })
                      }
                    />
                    <TextField
                      label="Runtime coverage evidence reference"
                      value={policy.coverageReference ?? ''}
                      onChange={(e) => setPolicy({ ...policy, coverageReference: e.target.value })}
                    />
                  </>
                )}
                <TextField
                  label="Effective at (ISO timestamp)"
                  value={policy.effectiveAt}
                  onChange={(e) => setPolicy({ ...policy, effectiveAt: e.target.value })}
                />
                <TextField
                  label="Audit reason"
                  value={policy.reason}
                  onChange={(e) => setPolicy({ ...policy, reason: e.target.value })}
                  required
                  multiline
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={busy}
            onClick={() => {
              setPrice(null);
              setPolicy(null);
            }}>
            Cancel
          </Button>
          <Button variant="contained" disabled={busy} onClick={save}>
            Publish version
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
